import * as React from 'react'
import { connect } from 'react-redux'
import { RouteComponentProps } from 'react-router'

import * as Yup from 'yup'
import classnames from 'classnames'
import { Form, Formik, FormikProps } from 'formik'

import { Button, ButtonGroup } from 'reactstrap'

import { RootState } from '../../store/rootReducer'
import { Termination } from '../../lib/interfaces/Termination.interface'
import TerminationFormData from './termination-flow/TerminationFormData.interface'

import Breadcrumbs from '../Breadcrumbs.component'
import TextArea from '../../common/TextArea.component'
import DatePickerCard from '../../common/DatePickerCard.component'

import TerminationReasonSelect from './termination-flow/components/TerminationReasonSelect.component'

import { formatName } from 'src/lib/helpers/format'

import * as terminationsActions from '../../store/terminations/actions'

const get = require('lodash/get')

interface RouteInfo {
  terminationId?: string
}

interface FormValues {
  reason: string
  comments: string
  voluntary: boolean
  lastDayOfWork: Date
}

export interface TerminationEditionPageProps extends RouteComponentProps<RouteInfo> {
  termination: Termination
  fetchTermination: (terminationId: string, next?: (err?: any, termination?: Termination) => void) => void
  updateTermination: (terminationId: string, termination: TerminationFormData, next?: (err?: any, termination?: Termination) => void) => void
}

interface TerminationEditionPageState {
  pageStatus: 'loading' | 'done'
}

class TerminationEditionPage extends React.PureComponent<TerminationEditionPageProps, TerminationEditionPageState> {
  constructor(props) {
    super(props)

    this.onSubmit = this.onSubmit.bind(this)

    this.state = {
      pageStatus: this.terminationId ? 'loading' : 'done'
    }
  }

  componentDidMount() {
    this.fetchTermination()
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.terminationId !== get(prevProps, 'match.params.terminationId')) {
      this.setState({ pageStatus: 'loading' }, this.fetchTermination)
    }
  }

  fetchTermination() {
    this.setState({ pageStatus: 'loading' }, () => {
      this.props.fetchTermination(this.terminationId, () => this.setState({ pageStatus: 'done' }))
    })
  }

  get terminationId() {
    return get(this.props.match, 'params.terminationId')
  }

  get employeeName() {
    const { termination } = this.props

    if (termination) {
      return formatName(termination.employee)
    }

    return null
  }

  get validationSchema() {
    return Yup.object().shape({
      lastDayOfWork: Yup.date().required()
    })
  }

  get initialValues(): FormValues|undefined {
    const { pageStatus } = this.state
    const { termination } = this.props

    if (this.terminationId && termination && pageStatus === 'done') {
      return {
        reason: termination.reason,
        comments: termination.comments,
        voluntary: termination.voluntary,
        lastDayOfWork: new Date(termination.lastDayOfWork)
      } as FormValues
    }

    return undefined
  }

  onSubmit(values) {
    this.props.updateTermination(this.terminationId, values as TerminationFormData, (err?: any, termination?: Termination) =>
      this.props.history.push(`/terminations/${this.terminationId}`)
    )
  }

  renderVoluntaryButton(formProps, title, voluntary) {
    const onClick = () => formProps.setFieldValue('voluntary', voluntary)

    const isSelected = formProps.values.voluntary === voluntary
    return (
      <Button
        onClick={onClick}
        active={isSelected}
        className="ggbutton"
        style={{ paddingX: '10px' }}
        color={isSelected ? 'primary' : 'light'}
      >
        {title}
      </Button>
    )
  }

  renderForm() {
    return (
      <Formik
        enableReinitialize
        onSubmit={this.onSubmit}
        initialValues={this.initialValues}
        validationSchema={this.validationSchema}
      >
        {(formProps: FormikProps<FormValues>) => {
          return (
            <Form>
              <div className="row no-gutters mt-3">
                <div className="col-12 col-md-6 col-lg-4 pr-md-3">
                  <div style={{ overflow: 'visible' }} className="ggcard p-3">
                    <h6 className="title flex-grow-1 mb-1">Voluntary</h6>
                    <ButtonGroup className="mt-2">
                      {this.renderVoluntaryButton(formProps, 'Voluntary', true)}
                      {this.renderVoluntaryButton(formProps, 'Involuntary', false)}
                    </ButtonGroup>
                  </div>
                </div>

                <div className="col-12 col-md-6 col-lg-4 pr-lg-3 mt-3 mt-md-0">
                  <DatePickerCard
                    title="Last day of work"
                    value={formProps.values.lastDayOfWork.toISOString()}
                    onChange={(value: Date) => formProps.setFieldValue('lastDayOfWork', value)}
                    cardClassName={classnames({ 'ggcard--error': !!formProps.errors.lastDayOfWork })}
                  />
                </div>

                <div className="col-12 col-md-6 col-lg-4">
                  <div style={{ overflow: 'visible' }} className="h-100 ggcard p-3">
                    <h6 className="title flex-grow-1 mb-1">Reason</h6>
                    <TerminationReasonSelect
                      selectType="normal"
                      value={formProps.values.reason}
                      onChange={(value: string) => formProps.setFieldValue('reason', value)}
                    />
                  </div>
                </div>

                <div className="col-12">
                  <div className="ggcard h-100 w-100 p-3 mt-3">
                    <h6 className="title">Comments</h6>

                    <TextArea
                      type="grey"
                      name="comments"
                      className="h-100 mt-2"
                      onChange={formProps.handleChange}
                      value={formProps.values.comments}
                    />
                  </div>
                </div>
              </div>

              <div className="row no-gutters mt-5">
                <div className="col-12">
                  <Button color="primary" className="w-100" disabled={!formProps.isValid}>
                    Save termination
                  </Button>
                </div>
              </div>
            </Form>
          )
        }}
      </Formik>
    )
  }

  render() {
    const { pageStatus } = this.state

      const breadcrumbs = [
      { name: 'Terminations', link: '/terminations' },
      { name: this.employeeName || 'Termination', link: `/terminations/${this.terminationId}` },
      { name: 'Edit', active: true }
    ]

    return (
      <div>
        <Breadcrumbs breadcrumbs={breadcrumbs} />

        {pageStatus === 'loading'
          ? <div className="p-3">Loading...</div>
          : (
            <div className="page accidentreport-page container-fluid">
              <section>
                <h5 className="title">Edit <b>{this.employeeName}</b>'s Termination</h5>
                {this.renderForm()}
              </section>
            </div>
          )
        }
      </div>
    )
  }
}

const mapStateToProps = (state: RootState) => ({
  positions: state.app.positions,
  termination: state.terminations.terminationDetails
})

const mapDispatchToProps = (dispatch) => ({
  fetchTermination: (terminationId: string, next?: (err?: any, termination?: Termination) => void) =>
    dispatch(terminationsActions.fetchTermination(terminationId, next)),
  updateTermination: (terminationId: string, termination: TerminationFormData, next?: (err?: any, termination?: Termination) => void) =>
    dispatch(terminationsActions.updateTermination(terminationId, termination, next))
})

export default connect(mapStateToProps, mapDispatchToProps)(TerminationEditionPage)



// WEBPACK FOOTER //
// ./src/routes/terminations/TerminationEdition.page.tsx